import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CronHealthCheck {
  id: string;
  job_name: string;
  expected_interval_minutes: number;
  last_healthy_execution: string | null;
  consecutive_failures: number;
  alert_threshold: number;
  is_healthy: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailDLQEntry {
  id: string;
  original_scheduled_email_id: string | null;
  to_email: string;
  template_key: string;
  variables: Record<string, string> | null;
  user_id: string | null;
  failure_count: number;
  last_error: string | null;
  first_failure_at: string;
  last_failure_at: string;
  next_retry_at: string | null;
  max_retries: number;
  should_retry: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  created_at: string;
}

export interface EmailSystemHealth {
  pending_emails: number;
  dlq_count: number;
  failed_24h: number;
  sent_24h: number;
  unhealthy_crons: Array<{
    job_name: string;
    consecutive_failures: number;
    last_healthy: string | null;
  }>;
  overall_healthy: boolean;
}

export function useCronHealthChecks() {
  return useQuery({
    queryKey: ['cron-health-checks'],
    queryFn: async (): Promise<CronHealthCheck[]> => {
      const { data, error } = await supabase
        .from('cron_health_checks')
        .select('*')
        .order('job_name');

      if (error) throw error;
      return data as CronHealthCheck[];
    },
  });
}

export function useEmailDLQ() {
  return useQuery({
    queryKey: ['email-dlq'],
    queryFn: async (): Promise<EmailDLQEntry[]> => {
      const { data, error } = await supabase
        .from('email_dead_letter_queue')
        .select('*')
        .is('resolved_at', null)
        .order('last_failure_at', { ascending: false });

      if (error) throw error;
      return data as EmailDLQEntry[];
    },
  });
}

export function useEmailSystemHealth() {
  return useQuery({
    queryKey: ['email-system-health'],
    queryFn: async (): Promise<EmailSystemHealth> => {
      const { data, error } = await supabase.rpc('get_email_system_health');
      
      if (error) throw error;
      return data as EmailSystemHealth;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useRetryDLQEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dlqId: string) => {
      const { data, error } = await supabase.rpc('retry_email_from_dlq', {
        p_dlq_id: dlqId
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; new_email_id?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to retry email');
      }
      
      return result;
    },
    onSuccess: () => {
      toast.success('Email agendado para retry');
      queryClient.invalidateQueries({ queryKey: ['email-dlq'] });
      queryClient.invalidateQueries({ queryKey: ['email-system-health'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-emails'] });
    },
    onError: (error) => {
      toast.error(`Erro ao fazer retry: ${error.message}`);
    },
  });
}

export function useResolveDLQEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dlqId, notes }: { dlqId: string; notes?: string }) => {
      const { error } = await supabase
        .from('email_dead_letter_queue')
        .update({
          resolved_at: new Date().toISOString(),
          resolution_notes: notes || 'Resolved manually',
          should_retry: false,
        })
        .eq('id', dlqId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Entrada resolvida');
      queryClient.invalidateQueries({ queryKey: ['email-dlq'] });
      queryClient.invalidateQueries({ queryKey: ['email-system-health'] });
    },
    onError: (error) => {
      toast.error(`Erro ao resolver: ${error.message}`);
    },
  });
}

export function useSendScheduledEmailNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      const { data, error } = await supabase.rpc('send_scheduled_email_now', {
        p_email_id: emailId
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to schedule email');
      }
      
      return result;
    },
    onSuccess: () => {
      toast.success('Email agendado para envio imediato');
      queryClient.invalidateQueries({ queryKey: ['scheduled-emails'] });
      queryClient.invalidateQueries({ queryKey: ['email-system-health'] });
    },
    onError: (error) => {
      toast.error(`Erro: ${error.message}`);
    },
  });
}

export function useRescheduleEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ emailId, newDate }: { emailId: string; newDate: Date }) => {
      const { data, error } = await supabase.rpc('reschedule_email', {
        p_email_id: emailId,
        p_new_date: newDate.toISOString()
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to reschedule email');
      }
      
      return result;
    },
    onSuccess: () => {
      toast.success('Email reagendado');
      queryClient.invalidateQueries({ queryKey: ['scheduled-emails'] });
    },
    onError: (error) => {
      toast.error(`Erro ao reagendar: ${error.message}`);
    },
  });
}

export function useCronJobLogs(jobName?: string, limit: number = 20) {
  return useQuery({
    queryKey: ['cron-job-logs', jobName, limit],
    queryFn: async () => {
      let query = supabase
        .from('cron_job_logs')
        .select('*')
        .order('executed_at', { ascending: false })
        .limit(limit);

      if (jobName) {
        query = query.eq('job_name', jobName);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}
